import { useMemo } from 'react';
import { Link } from 'react-router';
import type { RetroSnapshot } from '@retro/shared';
import { Avatar, EmptyState } from '../ui.js';
import { RotiPanel } from './RotiPanel.js';
import { ActionItemRow } from './ActionItemDialog.js';
import { TopicChipList } from './TopicChips.js';
import { downloadReportCsv, downloadReportMarkdown } from '../../lib/exportReport.js';
import { buildTopicBuckets } from '../../lib/reportBuckets.js';

/**
 * The closing report.
 *
 * Comments are regrouped by topic rather than by column, because after the fact
 * the useful question is "what themes keep coming up", not "which box did this
 * start in". The regrouping (and its per-unit vote counting) lives in
 * lib/reportBuckets.ts so it can be tested directly.
 */
export function ReportPhase({ snapshot, isSignedIn }: { snapshot: RetroSnapshot; isSignedIn: boolean }) {
  const buckets = useMemo(() => buildTopicBuckets(snapshot), [snapshot]);

  const created = new Date(snapshot.retro.createdAt).toLocaleDateString();

  return (
    <div className="thin-scroll flex-1 overflow-y-auto py-4">
      <div className="mx-auto max-w-3xl space-y-4 pb-10">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">Retrospective report</h1>
            <p className="text-sm text-ink/60">
              {created} · {snapshot.retro.templateName}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn text-sm"
              onClick={() => downloadReportMarkdown(snapshot)}
            >
              <span aria-hidden>⬇</span> Markdown
            </button>
            <button type="button" className="btn text-sm" onClick={() => downloadReportCsv(snapshot)}>
              <span aria-hidden>⬇</span> CSV
            </button>
          </div>
        </header>

        {snapshot.roti ? <RotiPanel roti={snapshot.roti} /> : null}

        <section className="card p-4">
          <p className="text-sm text-ink/60">{snapshot.retro.teamName}</p>
          <h2 className="text-lg font-semibold">{snapshot.retro.name}</h2>

          <h3 className="mt-4 text-sm font-semibold">Participants ({snapshot.participants.length})</h3>
          <ul className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {snapshot.participants.map((participant) => (
              <li key={participant.id} className="flex items-center gap-2">
                <Avatar emoji={participant.avatar} name={participant.name} size="sm" />
                <span className="truncate text-sm">
                  {participant.name}
                  {participant.id === snapshot.me.participantId ? ' (you)' : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="card overflow-hidden">
          <header className="flex items-center gap-2 border-b border-black/5 px-4 py-3">
            <h2 className="flex-1 font-semibold">Action items ({snapshot.actionItems.length})</h2>
            {isSignedIn ? (
              <Link
                to={`/teams/${snapshot.retro.teamId}`}
                className="text-sm font-medium text-brand-600 hover:underline"
              >
                Go to the team board
              </Link>
            ) : null}
          </header>

          {snapshot.actionItems.length === 0 ? (
            <EmptyState emoji="📋" title="No actions were captured">
              A retrospective without actions rarely changes anything — worth a follow-up.
            </EmptyState>
          ) : (
            <ul className="space-y-2 p-3">
              {snapshot.actionItems.map((action) => (
                <ActionItemRow key={action.id} action={action} />
              ))}
            </ul>
          )}
        </section>

        <section className="space-y-3">
          <h2 className="px-1 font-semibold">Discussions by topic</h2>

          {buckets.map((bucket) => (
            <div key={bucket.id} className="card overflow-hidden">
              <header className="flex items-center gap-2 border-b border-black/5 px-4 py-2.5">
                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: bucket.color }} aria-hidden />
                <h3 className="flex-1 text-sm font-semibold">{bucket.name}</h3>
                <span
                  className="text-sm tabular-nums text-ink/60"
                  aria-label={`${bucket.votes} ${bucket.votes === 1 ? 'vote' : 'votes'}`}
                >
                  {bucket.votes} <span aria-hidden>👍</span>
                </span>
              </header>

              <ul className="divide-y divide-black/5">
                {bucket.entries.map(({ comment, votes, columnTitle, groupTitle }) => (
                  <li key={`${bucket.id}-${comment.id}`} className="px-4 py-3">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 w-8 shrink-0 text-sm font-semibold tabular-nums text-ink/60">
                        {votes}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-ink/50">
                          {columnTitle}
                          {groupTitle ? ` · ${groupTitle}` : ''}
                        </p>
                        <p className="mt-0.5 whitespace-pre-wrap break-words text-sm">{comment.text}</p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-2">
                          {comment.isAnonymous ? (
                            <span className="text-xs text-ink/50">Anonymous</span>
                          ) : (
                            <span className="flex items-center gap-1.5">
                              <Avatar
                                emoji={comment.author?.avatar ?? ''}
                                name={comment.author?.name ?? ''}
                                size="xs"
                              />
                              <span className="text-xs text-ink/55">{comment.author?.name}</span>
                            </span>
                          )}
                          <TopicChipList topicIds={comment.topicIds} topics={snapshot.topics} />
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
